import { Add, Search } from '@mui/icons-material';
import {
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/Button';
import { useAlertsStore } from '@/configs/store';
import { IS_V2_WRITE_ENABLED } from '@/configs/v2Write';
import { useLocalStore } from '@/hooks/useLocalStore';
import { useToggleState } from '@/hooks/useToggleState';
import { trackerUrls } from '@/tracker/config/urls';
import { BoardSettingsDialog } from '@/tracker/features/boardSettings';
import { Loader } from '@/tracker/ui/Loader';
import { LoadMoreTrigger } from '@/tracker/ui/LoadMoreTrigger';

import { BoardsStore } from './model/BoardsStore';

import s from './BoardsPage.module.scss';

/** Каталог досок, как «Доски задач» в Яндекс Трекере */
const BoardsPage = observer(() => {
  const alertsStore = useAlertsStore();
  const navigate = useNavigate();
  const { store } = useLocalStore(() => new BoardsStore({ alertsStore }));
  const creation = useToggleState();

  useEffect(() => {
    store.load();
  }, [store]);

  const isEmpty = store.boards.length === 0;

  return (
    <div className={s.page}>
      <div className={s.page__header}>
        <Typography variant="h5" fontWeight="bold">
          Доски
        </Typography>
        <div className={s.page__actions}>
          <TextField
            size="small"
            sx={{ width: 320 }}
            placeholder="Название доски"
            value={store.query}
            onChange={(event) => store.setQuery(event.target.value)}
            slotProps={{
              htmlInput: { 'aria-label': 'Поиск досок' },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          {IS_V2_WRITE_ENABLED && (
            <Button startIcon={<Add />} onClick={creation.open}>
              Создать доску
            </Button>
          )}
        </div>
      </div>

      {isEmpty && !store.isLoaded && !store.isError && <Loader />}

      {store.isError && isEmpty && (
        <div className={s.page__empty}>
          <Typography color="text.secondary">Не удалось загрузить доски</Typography>
          <Button onClick={store.load}>Повторить</Button>
        </div>
      )}

      {store.isLoaded && isEmpty && (
        <div className={s.page__empty}>
          <Typography color="text.secondary">
            {store.query ? 'Ничего не нашлось' : 'Досок пока нет'}
          </Typography>
        </div>
      )}

      {!isEmpty && (
        <>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Название</TableCell>
                <TableCell>Владелец</TableCell>
                <TableCell>Текущий спринт</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {store.boards.map((board) => (
                <TableRow
                  key={board.id}
                  hover
                  className={s.row}
                  onClick={() => navigate(trackerUrls.board(board.id))}
                >
                  <TableCell>
                    <Link
                      to={trackerUrls.board(board.id)}
                      className={s.row__name}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {board.name}
                    </Link>
                  </TableCell>
                  <TableCell>{board.owner?.displayName ?? '—'}</TableCell>
                  <TableCell className={s.row__secondary}>
                    {board.activeSprint?.name ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className={s.page__footer}>
            <Typography variant="body2" color="text.secondary">
              Показано {store.boards.length} из {store.totalSize}
            </Typography>
            <LoadMoreTrigger
              hasMore={store.hasMore}
              isLoading={store.isLoading}
              isError={store.isError}
              onLoadMore={store.loadMore}
            />
          </div>
        </>
      )}

      <AnimatePresence>
        {creation.opened && (
          <BoardSettingsDialog
            board={null}
            onSaved={(board) => navigate(trackerUrls.board(board.id))}
            onClose={creation.close}
          />
        )}
      </AnimatePresence>
    </div>
  );
});

export { BoardsPage };
